import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, Mail, Phone, Building2, Calendar, Trash2, Eye, Filter, Shield, MessageSquare, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { adminApi, formatApiErrorDetail } from '../utils/api';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [notes, setNotes] = useState('');
  const [stats, setStats] = useState({ total: 0, pending: 0, replied: 0, archived: 0 });

  useEffect(() => {
    fetchContacts();
  }, [statusFilter]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const statusParam = statusFilter === 'all' ? null : statusFilter;
      const { data } = await adminApi.getContacts(1, 100, statusParam);
      setContacts(data.data.contacts);
      
      // Calculate stats
      const allContacts = await adminApi.getContacts(1, 1000);
      const all = allContacts.data.data.contacts;
      setStats({
        total: all.length,
        pending: all.filter(c => c.status === 'pending').length,
        replied: all.filter(c => c.status === 'replied').length,
        archived: all.filter(c => c.status === 'archived').length,
      });
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await adminApi.updateContact(id, { status: newStatus });
      toast.success('Status updated');
      fetchContacts();
      if (selectedContact?.id === id) {
        setSelectedContact({ ...selectedContact, status: newStatus });
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update');
    }
  };

  const handleUpdateNotes = async () => {
    if (!selectedContact) return;
    try {
      await adminApi.updateContact(selectedContact.id, { notes });
      toast.success('Notes updated');
      fetchContacts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update notes');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await adminApi.deleteContact(id);
      toast.success('Contact deleted');
      setSelectedContact(null);
      fetchContacts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to delete');
    }
  };

  const openContactDetail = (contact) => {
    setSelectedContact(contact);
    setNotes(contact.notes || '');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30';
      case 'replied': return 'bg-green-500/20 text-green-500 border-green-500/30';
      case 'archived': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-blue-500/20 text-blue-500 border-blue-500/30';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#0f0f10]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1a1a1a] to-[#2d1f1f] border-b border-[#D4AF37]/20 sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-[#8B1538] to-[#A52A2A] rounded-sm flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Dashboard</h1>
              <p className="text-xs text-[#D4AF37]">INDOAXIUM Software Services</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right">
              <p className="text-sm text-white font-medium">{user?.name || 'Admin'}</p>
              <p className="text-xs text-gray-400">{user?.email}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              data-testid="admin-logout-button"
              className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total', value: stats.total, color: '#D4AF37' },
            { label: 'Pending', value: stats.pending, color: '#eab308' },
            { label: 'Replied', value: stats.replied, color: '#22c55e' },
            { label: 'Archived', value: stats.archived, color: '#6b7280' },
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-6 rounded-sm border border-[#D4AF37]/20"
              data-testid={`stat-${stat.label.toLowerCase()}`}
            >
              <p className="text-sm text-gray-400 mb-2">{stat.label}</p>
              <p className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <h2 className="text-2xl font-bold text-white">Contact Submissions</h2>
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-[#D4AF37]" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-[#1a1a1a] border-[#D4AF37]/20 text-white" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-[#D4AF37]/20 text-white">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="replied">Replied</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={fetchContacts}
              variant="outline"
              size="sm"
              data-testid="refresh-button"
              className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Contacts List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-12 rounded-sm border border-[#D4AF37]/20 text-center">
            <MessageSquare className="w-16 h-16 text-[#D4AF37]/30 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No contact submissions yet</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {contacts.map((contact, idx) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ scale: 1.005 }}
                onClick={() => openContactDetail(contact)}
                data-testid={`contact-card-${contact.id}`}
                className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-6 rounded-sm border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all duration-300 cursor-pointer group"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-white group-hover:text-[#D4AF37] transition-colors">
                        {contact.name}
                      </h3>
                      <Badge className={`${getStatusColor(contact.status)} border`}>
                        {contact.status}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {contact.email}
                      </span>
                      {contact.company && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {contact.company}
                        </span>
                      )}
                      {contact.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {contact.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(contact.submittedAt)}
                      </span>
                    </div>
                    <p className="text-gray-300 mt-3 line-clamp-2">{contact.message}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                      onClick={(e) => { e.stopPropagation(); openContactDetail(contact); }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Contact Detail Dialog */}
      <Dialog open={!!selectedContact} onOpenChange={(open) => !open && setSelectedContact(null)}>
        <DialogContent className="bg-[#1a1a1a] border-[#D4AF37]/20 text-white max-w-2xl" data-testid="contact-detail-dialog">
          {selectedContact && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl text-white flex items-center gap-3">
                  {selectedContact.name}
                  <Badge className={`${getStatusColor(selectedContact.status)} border`}>
                    {selectedContact.status}
                  </Badge>
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  Submitted on {formatDate(selectedContact.submittedAt)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-[#0f0f10] p-4 rounded-sm border border-[#D4AF37]/10">
                    <p className="text-xs text-gray-400 mb-1">Email</p>
                    <p className="text-white">{selectedContact.email}</p>
                  </div>
                  {selectedContact.company && (
                    <div className="bg-[#0f0f10] p-4 rounded-sm border border-[#D4AF37]/10">
                      <p className="text-xs text-gray-400 mb-1">Company</p>
                      <p className="text-white">{selectedContact.company}</p>
                    </div>
                  )}
                  {selectedContact.phone && (
                    <div className="bg-[#0f0f10] p-4 rounded-sm border border-[#D4AF37]/10">
                      <p className="text-xs text-gray-400 mb-1">Phone</p>
                      <p className="text-white">{selectedContact.phone}</p>
                    </div>
                  )}
                </div>

                {/* Message */}
                <div className="bg-[#0f0f10] p-4 rounded-sm border border-[#D4AF37]/10">
                  <p className="text-xs text-gray-400 mb-2">Message</p>
                  <p className="text-white leading-relaxed">{selectedContact.message}</p>
                </div>

                {/* Status Change */}
                <div>
                  <label className="text-sm text-gray-300 mb-2 block">Change Status</label>
                  <Select
                    value={selectedContact.status}
                    onValueChange={(value) => handleStatusChange(selectedContact.id, value)}
                  >
                    <SelectTrigger className="bg-[#0f0f10] border-[#D4AF37]/20 text-white" data-testid="status-change-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-[#D4AF37]/20 text-white">
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="replied">Replied</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-sm text-gray-300 mb-2 block">Internal Notes</label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add internal notes..."
                    className="bg-[#0f0f10] border-[#D4AF37]/20 text-white focus:border-[#D4AF37]"
                    rows={3}
                    data-testid="notes-textarea"
                  />
                  <Button
                    onClick={handleUpdateNotes}
                    size="sm"
                    className="mt-2 bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30"
                    data-testid="save-notes-button"
                  >
                    Save Notes
                  </Button>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(selectedContact.id)}
                  data-testid="delete-contact-button"
                  className="bg-red-900/50 hover:bg-red-900 text-red-100"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button
                  onClick={() => window.open(`mailto:${selectedContact.email}`)}
                  className="bg-gradient-to-r from-[#D4AF37] to-[#F4E4B0] text-[#1a1a1a]"
                  data-testid="reply-email-button"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Reply via Email
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
